import React, { useEffect, useState } from 'react';
import { CircularProgress, Dialog, DialogContent, IconButton, Typography, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axiosInstant from '../../api';
import { getProductPdpUrlScore } from '../../api/endpoints';
import { PdpAuditReport } from '../PdpAudit/PdpAuditReport';
import { PdpAudit } from '../PdpAudit/types';
import { useUser } from '../../hooks/useUser';
import { useNetworkScope } from '../../hooks/useNetworkScope';
import { isInternal } from '../../utils';
import { PdpUrlScoreDetail } from '../../types/rmn';

// Renders one scoring run's stored audit report (TDC-1499 phase 3). Every
// pdp_url_scores row carries the full audit blob — the same PdpAudit shape
// the single-PDP audit endpoints produce — so the classic report renderer
// works as-is with a minimal meta wrapper.

interface Props {
    productId: number;
    pdpUrlId: number;
    scoreId: number;
    pdpUrl: string;
    onClose: () => void;
}

export function PdpScoreReportDialog({ productId, pdpUrlId, scoreId, pdpUrl, onClose }: Props) {
    const { role } = useUser();
    const [detail, setDetail] = useState<PdpUrlScoreDetail | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        axiosInstant
            .get(getProductPdpUrlScore(productId, pdpUrlId, scoreId))
            .then((res) => {
                if (!cancelled) setDetail((res.data?.data || res.data) as PdpUrlScoreDetail);
            })
            .catch(() => {
                if (!cancelled) setError("Couldn't load this report. Please try again.");
            });
        return () => {
            cancelled = true;
        };
    }, [productId, pdpUrlId, scoreId]);

    const { basePath, inWorkspace } = useNetworkScope();
    const internal = isInternal(role);
    // Inside a brand-portal workspace everyone uses the brand's wizard;
    // internal staff elsewhere get the internal proposal wizard.
    const internalWizard = internal && !inWorkspace;
    const audit = detail?.report && !(detail.report as any).error ? (detail.report as unknown as PdpAudit) : null;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md">
            <IconButton
                onClick={onClose}
                sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
                aria-label="Close report"
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ p: 0 }}>
                {!detail && !error && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {error && (
                    <Typography variant="body2" color="error" sx={{ textAlign: "center", py: 8 }}>
                        {error}
                    </Typography>
                )}
                {detail && !error && !audit && (
                    <Typography variant="body2" sx={{ textAlign: "center", py: 8, color: "var(--muted)" }}>
                        Report data isn&apos;t available for this run.
                    </Typography>
                )}
                {audit && (
                    <PdpAuditReport
                        data={{
                            audit,
                            meta: {
                                url: pdpUrl,
                                finalUrl: pdpUrl,
                                title: '',
                                metaDescription: '',
                                jsonLdBlockCount: 0,
                                geminiStatus: 'success',
                                geminiStatusCode: null,
                                geminiError: null,
                            },
                        }}
                        isLoggedIn
                        showEmailAction={false}
                        proposalPath={internalWizard ? '/proposal' : `${basePath}/opportunities/strategies/new`}
                        proposalCtaLabel={internalWizard ? 'Create Proposal' : 'Create Strategy'}
                        productRef={{ desireid: productId, pdpUrlScoreId: scoreId }}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
